<template>
  <b-modal 
    id="modal-tna-mitra-detail" 
    centered 
    hide-footer 
    title="Training Detail"
    size="xl">
    <!-- Loading State -->
    <div v-if="isLoading" class="text-center py-5">
      <div class="spinner-border text-danger" role="status">
        <span class="sr-only">Loading...</span>
      </div>
      <p class="mt-2 text-muted">Loading training detail...</p>
    </div>

    <!-- Content -->
    <div v-else-if="courseData">
      <!-- Course Content -->
      <div class="row g-0">
        <!-- Full Width - Course Info -->
        <div class="col-md-12">
          <!-- Header Section -->
          <div class="row mb-4">
            <div class="col-md-7 d-flex flex-column justify-content-between">
              <div class="d-flex flex-column">
                <!-- Status Badge -->
                <div class="mb-3">
                  <span :class="courseData && courseData.is_active ? 'badge bg-success' : 'badge bg-warning'" style="padding: 0.35rem 0.75rem;">
                    <i class="fe fe-info me-1"></i>
                    {{ courseData.training_status || '-' }}
                  </span>
                </div>
                
                <!-- Course Title -->
                <h3 class="mb-3 font-weight-bold" style="color: #1a202c;">
                  {{ courseData.title || '-' }}
                </h3>

                <!-- Category Badges -->
                <div class="mb-4 d-flex gap-2">
                  <span class="badge-icon bg-danger-soft text-danger">
                    <i :class="getTrainingTypeIcon(courseData.training_type)"></i>
                  </span>
                  <span class="badge-custom bg-warning-soft text-warning">
                    {{ courseData?.role_competency || '-' }}
                  </span>
                  <span class="badge-custom bg-info-soft text-info">
                    {{ courseData?.role_competency_type || '-' }}
                  </span>
                </div>
              </div>

              <!-- Stats -->
              <div class="row p-3 rounded" style="background-color: #FAFAFA;">
                <div class="col-4">
                  <div class="d-flex align-items-center text-muted">
                    <i class="fe fe-clock me-2" style="font-size: 1.1rem;"></i>
                    <div>
                      <small class="d-block" style="font-size: 0.7rem;">Learning Hours</small>
                      <strong class="text-dark">{{ courseData.hour ? Number(courseData.hour) : '-' }} hrs</strong>
                    </div>
                  </div>
                </div>
                <div class="col-4">
                  <div class="d-flex align-items-center text-muted">
                    <i class="fe fe-dollar-sign me-2" style="font-size: 1.1rem;"></i>
                    <div>
                      <small class="d-block" style="font-size: 0.7rem;">Estimated Price</small>
                      <strong class="text-dark">IDR {{ courseData.closest_batch_total_price ? compactNumberFormatter(courseData.closest_batch_total_price) : '-' }}</strong>
                    </div>
                  </div>
                </div>
                <div class="col-4">
                  <div class="d-flex align-items-center text-muted">
                    <i class="fe fe-users me-2" style="font-size: 1.1rem;"></i>
                    <div>
                      <small class="d-block" style="font-size: 0.7rem;">Participants</small>
                      <strong class="text-dark">{{ participants.length }}</strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Right Side - Course Image -->
            <div class="col-md-5">
              <img 
                :src="courseData && courseData.cover_image_path ? `${imageBaseUrl}/attachments/${courseData.cover_image_path}` : 'https://dummyimage.com/300x200/FAFAFA/000000&text=Course'" 
                alt="Course Thumbnail"
                class="w-100 rounded"
                style="height: 280px; object-fit: cover;"
                @error="handleImageError">
            </div>
          </div>

          <!-- Tabs -->
          <ul class="nav nav-tabs mb-3" role="tablist">
            <li class="nav-item">
              <a class="nav-link active" data-bs-toggle="tab" href="#description">
                Description
              </a>
            </li>
            <li class="nav-item">
              <a class="nav-link" data-bs-toggle="tab" href="#attributes">
                Attributes
              </a>
            </li>
            <li class="nav-item">
              <a class="nav-link" data-bs-toggle="tab" href="#platform">
                Platform
              </a>
            </li>
            <li class="nav-item">
              <a class="nav-link" data-bs-toggle="tab" href="#participants">
                Participants
              </a>
            </li>
          </ul>

          <!-- Tab Content -->
          <div class="tab-content">
            <!-- Description Tab -->
            <div class="tab-pane fade show active" id="description">
              <p class="mb-3" style="line-height: 1.8; font-size: 0.9rem;">
                {{ courseData.description || 'No description available.' }}
              </p>
              
              <!-- Related Competencies -->
              <div class="mt-4" v-if="courseData.competency && courseData.competency.length > 0">
                <h6 class="font-weight-bold mb-3">Related Competencies</h6>
                <div class="d-flex gap-2 flex-wrap">
                  <span 
                    v-for="(comp, index) in courseData.competency" 
                    :key="index"
                    class="badge bg-white text-dark border px-3 py-2" 
                    style="font-weight: 500;">
                    {{ comp.name }} <span class="text-muted">|</span> <i class="fe fe-star text-warning"></i> {{ comp.proficiency ? comp.proficiency[0] : '-' }}
                  </span>
                </div>
                <p class="text-muted mt-3 mb-0" style="font-size: 0.75rem;">
                  **Notes: <i class="fe fe-star text-warning"></i> Levels of proficiency 1-5
                </p>
              </div>
            </div>

            <!-- TabAttributes -->
            <div class="tab-pane fade" id="attributes">
              <div class="row">
                <!-- Left Column -->
                <div class="col-md-6">
                  <!-- Course Method -->
                  <div class="mb-4 py-2 px-3 rounded" style="background-color: #fafafa;">
                    <label class="text-muted small mb-2">Course Method</label>
                    <p class="text-dark font-weight-bold mb-0">{{ courseData?.batches[0]?.training_method || '-' }}</p>
                  </div>

                  <!-- Course Category -->
                  <div class="mb-4 py-2 px-3 rounded" style="background-color: #fafafa;">
                    <label class="text-muted small mb-2">Course Category</label>
                    <p class="text-dark font-weight-bold mb-0">{{ courseData.training_category || '-' }}</p>
                  </div>

                  <!-- Course URL -->
                  <div class="mb-4 py-2 px-3 rounded" style="background-color: #fafafa;">
                    <label class="text-muted small mb-2">Course URL</label>
                    <div class="d-flex align-items-center gap-2">
                      <a v-if="courseData.website"
                         :href="courseData.website" 
                         target="_blank" 
                         class="text-primary text-truncate" 
                         style="max-width: 80%; text-decoration: none;">
                        {{ courseData.website }}
                      </a>
                      <span v-else class="text-muted">-</span>
                      <a v-if="courseData.website"
                        :href="courseData.website"
                        target="_blank"
                        class="btn btn-sm btn-danger d-flex align-items-center justify-content-center" 
                        style="width: 32px; height: 32px; padding: 0; border-radius: 4px;">
                        <i class="fe fe-send" style="font-size: 14px;"></i>
                      </a>
                    </div>
                  </div>

                  <!-- Department -->
                  <div class="mb-4 py-2 px-3 rounded" style="background-color: #fafafa;">
                    <label class="text-muted small mb-2">Department</label>
                    <p class="text-dark font-weight-bold mb-0">{{ courseData.department || '-' }}</p>
                  </div>

                  <!-- Division -->
                  <div class="mb-4 py-2 px-3 rounded" style="background-color: #fafafa;">
                    <label class="text-muted small mb-2">Division</label>
                    <p class="text-dark font-weight-bold mb-0">{{ courseData.division || '-' }}</p>
                  </div>
                </div>

                <!-- Right Column -->
                <div class="col-md-6">
                  <!-- Course Type -->
                  <div class="mb-4 py-2 px-3 rounded" style="background-color: #fafafa;">
                    <label class="text-muted small mb-2">Course Type</label>
                    <p class="text-dark font-weight-bold mb-0">{{ courseData.training_type_name || '-' }}</p>
                  </div>

                  <!-- Certificate Validity Period -->
                  <div class="mb-4 py-2 px-3 rounded" style="background-color: #fafafa;">
                    <label class="text-muted small mb-2">Certificate Validity Period (Months)</label>
                    <p class="text-dark font-weight-bold mb-0">{{ courseData.certificate_validity_period || '-' }}</p>
                  </div>

                  <!-- Course Scope -->
                  <div class="mb-4 py-2 px-3 rounded" style="background-color: #fafafa;">
                    <label class="text-muted small mb-2">Course Scope</label>
                    <div>
                      <span v-for="scope in courseData.training_scope" :key="scope" class="badge badge-dark font-weight-bold mr-1">
                        {{ scope }}
                      </span>
                    </div>
                  </div>

                  <!-- STEM Category -->
                  <div class="mb-4 py-2 px-3 rounded" style="background-color: #fafafa;">
                    <label class="text-muted small mb-2">STEM Category</label>
                    <p class="text-dark font-weight-bold mb-0">{{ courseData.stem_category || '-' }}</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Platform Tab -->
            <div class="tab-pane fade" id="platform">
              <div class="row">
                <!-- Left Column -->
                <div class="col-md-6">
                  <!-- Platform Name -->
                  <div class="mb-4 py-2 px-3 rounded" style="background-color: #fafafa;">
                    <label class="text-muted small mb-2">Platform Name</label>
                    <p class="text-dark font-weight-bold mb-0">{{ courseData.platform || '-' }}</p>
                  </div>

                  <!-- Training Provider (LDP) -->
                  <div class="mb-4 py-2 px-3 rounded" style="background-color: #fafafa;">
                    <label class="text-muted small mb-2">Training Provider</label>
                    <p class="text-dark font-weight-bold mb-0">{{ courseData.provider_name || '-' }}</p>
                  </div>

                  <!-- Provider Website -->
                  <div class="mb-4 py-2 px-3 rounded" style="background-color: #fafafa;">
                    <label class="text-muted small mb-2">Provider Website</label>
                    <p class="text-dark font-weight-bold mb-0">{{ courseData.provider_website || '-' }}</p>
                  </div>

                  <!-- Email -->
                  <div class="mb-4 py-2 px-3 rounded" style="background-color: #fafafa;">
                    <label class="text-muted small mb-2">Email</label>
                    <p class="text-dark font-weight-bold mb-0">{{ courseData.provider_email || '-' }}</p>
                  </div>
                </div>

                <!-- Right Column -->
                <div class="col-md-6">
                  <!-- Provider Name (LSP) -->
                  <div class="mb-4 py-2 px-3 rounded" style="background-color: #fafafa;">
                    <label class="text-muted small mb-2">Provider Name (LSP)</label>
                    <p class="text-dark font-weight-bold mb-0">{{ courseData.lsp_name || '-' }}</p>
                  </div>

                  <!-- Address -->
                  <div class="mb-4 py-2 px-3 rounded" style="background-color: #fafafa;">
                    <label class="text-muted small mb-2">Address</label>
                    <p class="text-dark font-weight-bold mb-0">{{ courseData.provider_address || '-' }}</p>
                  </div>

                  <!-- Contact Person -->
                  <div class="mb-4 py-2 px-3 rounded" style="background-color: #fafafa;">
                    <label class="text-muted small mb-2">Contact Person</label>
                    <p class="text-dark font-weight-bold mb-0">{{ courseData.provider_contact_person || '-' }}</p>
                  </div>

                  <!-- Phone -->
                  <div class="mb-4 py-2 px-3 rounded" style="background-color: #fafafa;">
                    <label class="text-muted small mb-2">Phone</label>
                    <p class="text-dark font-weight-bold mb-0">{{ courseData.provider_phone || '-' }}</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Participants Tab -->
            <div class="tab-pane fade" id="participants">
              <div class="d-flex justify-content-end gap-2 pb-2">
                <button
                  class="btn btn-sm btn-outline-info"
                  @click="downloadTemplate(courseData.id)">
                  <i class="fe fe-download me-1"></i>
                  Download Template
                </button>
                <button
                  class="btn btn-sm btn-success"
                  @click="showUploadModal">
                  <i class="fe fe-upload me-1"></i>
                  Upload File
                </button>
              </div>

              <div class="row mb-3">
                <div class="col-md-4">
                  <div class="form-group">
                    <label class="form-label">Status</label>
                    <v-select
                      v-model="selectedStatus"
                      :options="participantStatusOptions"
                      :reduce="option => option.value"
                      label="label"
                      placeholder="Semua status"
                      :clearable="true">
                    </v-select>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="form-group">
                    <label class="form-label">Approval</label>
                    <v-select
                      v-model="selectedApprovalStep"
                      :options="approvalStepOptions"
                      :reduce="option => option.value"
                      label="label"
                      placeholder="Semua approval"
                      :clearable="true">
                    </v-select>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="form-group">
                    <label class="form-label">Course Type</label>
                    <v-select
                      v-model="selectedCourseType"
                      :options="courseTypeOptions"
                      :reduce="option => option.value"
                      label="label"
                      placeholder="Semua tipe"
                      :clearable="true">
                    </v-select>
                  </div>
                </div>
              </div>

              <div
                v-for="year in planningYears"
                :key="`planning-${year}`"
                class="planning-section">
                <div class="planning-year-title mb-3">
                  <div class="planning-year-icon">
                    <i class="fe fe-calendar"></i>
                  </div>
                  <span class="planning-year-text mb-0">{{ year }} Planning</span>
                </div>

                <!-- Action Buttons -->
                <div class="d-flex justify-content-between align-items-center mb-3">
                  <h6 class="font-weight-bold mb-0">Training Participants</h6>
                  <div class="d-flex gap-2">
                    <button
                      class="btn btn-sm btn-primary"
                      @click="openAddParticipantModal(year)">
                      <i class="fe fe-plus me-1"></i>
                      Add Participant
                    </button>
                  </div>
                </div>

                <!-- Participants Table -->
                <vue-good-table
                  :columns="participantColumns"
                  :rows="getFilteredParticipantsByYear(year)"
                  :line-numbers="true"
                  :search-options="{
                    enabled: false
                  }"
                  :pagination-options="{
                    enabled: true,
                    perPage: 10,
                    perPageDropdown: [10, 25, 50, 100],
                    dropdownAllowAll: false
                  }"
                  styleClass="vgt-table striped"
                >
                  <!-- Empty State -->
                  <template slot="emptystate">
                    <div class="text-center py-5">
                      <i class="fe fe-inbox text-muted" style="font-size: 3rem;"></i>
                      <p class="text-muted mt-2 mb-0">Belum ada peserta yang ditambahkan untuk {{ year }}.</p>
                    </div>
                  </template>

                  <!-- Employee Name Column -->
                  <template slot="table-row" slot-scope="props">
                    <span v-if="props.column.field === 'employee_name'">
                      <span class="font-weight-bold">{{ props.row.employee_name }}</span>
                      <span
                        class="badge ms-1"
                        :class="props.row.is_mandatory ? 'bg-info' : 'bg-secondary'"
                        style="font-size: 0.65rem;">
                        {{ trainingRequirementLabel(props.row) }}
                      </span>
                      <span
                        class="badge ms-1"
                        :class="participantStatusBadgeClass(props.row)"
                        style="font-size: 0.65rem;">
                        {{ participantStatusLabel(props.row) }}
                      </span>
                    </span>

                    <!-- Action Column -->
                    <span v-else-if="props.column.field === 'action'">
                      <button
                        class="btn btn-sm btn-danger"
                        @click="confirmDeleteParticipant(props.row, year)"
                        :disabled="isParticipantLocked(props.row)"
                        :title="participantDeleteTitle(props.row)">
                        <i class="fe fe-trash-2"></i>
                      </button>
                    </span>

                    <!-- Default -->
                    <span v-else>
                      {{ props.formattedRow[props.column.field] }}
                    </span>
                  </template>
                </vue-good-table>
              </div>

              <!-- Submit Button -->
              <div class="mt-4 d-flex justify-content-end">
                <button 
                  class="btn btn-primary px-4"
                  @click="submitToManager"
                  :disabled="participants.length === 0">
                  <i class="fe fe-send me-2"></i>
                  Submit
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modals -->
    <modal-add-participant 
      :courseId="courseData ? courseData.id : null"
      :planningYear="selectedPlanningYear"
      :selectedCompany="selectedCompany"
    />
    
    <modal-delete-participant
      :participant="participantToDelete"
      @confirm-delete="onDeleteParticipant"
    />

    <!-- Upload Modal -->
    <b-modal 
      id="modal-upload-participant" 
      centered 
      hide-footer 
      title="Upload Participant File"
      size="lg">
      <form @submit.prevent="uploadFile()">
        <div class="row">
          <div class="col-12">
            <div class="alert alert-warning">
              <small>
                <i class="fe fe-info me-1"></i>
                Upload CSV or Excel file containing participant data. Make sure the file follows the template format.
              </small>
            </div>
          </div>
          <div class="col-12">
            <div class="form-group">
              <label>Select File <span class="text-danger">*</span></label>
              <input
                ref="fileInput"
                type="file"
                class="form-control"
                accept=".xlsx"
                @change="onFileChange"
                required />
              <small class="text-muted">Accepted formats:, .xlsx (max 5MB)</small>
            </div>
          </div>
          <div class="col-12" v-if="selectedFile">
            <div class="form-group">
              <label>File Preview:</label>
              <div class="card">
                <div class="card-body">
                  <div class="d-flex align-items-center">
                    <i class="fe fe-file-text text-success me-2" style="font-size: 24px;"></i>
                    <div>
                      <strong>{{ selectedFile.name }}</strong><br>
                      <small class="text-muted">{{ formatFileSize(selectedFile.size) }}</small>
                    </div>
                    <button 
                      type="button" 
                      class="btn btn-sm btn-danger ms-auto"
                      @click="clearFile()">
                      <i class="fe fe-x"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div class="form-group text-right">
          <button
            type="button"
            class="btn btn-secondary me-2"
            @click="$bvModal.hide('modal-upload-participant')">
            Batal
          </button>
          <button
            class="btn btn-primary"
            type="submit"
            :disabled="!selectedFile || isUploading">
            <span
              v-if="isUploading"
              class="spinner-border spinner-border-sm me-2"
              role="status"
              aria-hidden="true"></span>
            {{ isUploading ? 'Mengupload...' : 'Upload' }}
          </button>
        </div>
      </form>
    </b-modal>
  </b-modal>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { compactNumberFormatter } from '@/utils/currencyFormatter';
import ModalAddParticipant from './modal-add-participant.vue';
import ModalDeleteParticipant from './modal-delete-participant.vue';
import {
  APPROVAL_STEP_SLUG_OPTIONS,
  COURSE_TYPE_OPTIONS,
  PARTICIPANT_STATUS_OPTIONS,
  buildMitraFilterParams
} from '@/constants/mitraFilters';

export default {
  name: 'ModalTNAMitraDetail',
  components: {
    ModalAddParticipant,
    ModalDeleteParticipant
  },
  props: {
    selectedCourse: {
      type: Object,
      default: null
    },
    selectedCompany: {
      type: String,
      default: null
    }
  },
  computed: {
    ...mapGetters('tnaMitra', {
      courseDetail: 'courseDetail',
      isLoading: 'loading',
      participants: 'participants',
      participantLoading: 'participantLoading'
    }),
    currentYear() {
      return new Date().getFullYear();
    },
    planningYears() {
      return Array.from({ length: 5 }, (_, index) => this.currentYear + index);
    },
    newParticipants() {
      return (this.participants || []).filter(participant => participant?.is_new === true);
    },
    courseData() {
      return this.courseDetail || this.selectedCourse;
    }
  },
  data() {
    return {
      imageBaseUrl: process.env.VUE_APP_API_URL_UGEMS_UNIV,
      participantToDelete: null,
      selectedPlanningYear: new Date().getFullYear(),
      selectedFile: null,
      isUploading: false,
      selectedStatus: null,
      selectedApprovalStep: null,
      selectedCourseType: null,
      uploadForm: {
        company: null
      },
      participantStatusOptions: PARTICIPANT_STATUS_OPTIONS,
      approvalStepOptions: APPROVAL_STEP_SLUG_OPTIONS,
      courseTypeOptions: COURSE_TYPE_OPTIONS,
      companyOptions: [
        'PT Bank Index Selatan',
        'PT Bank Index International',
        'PT Index Finance',
        'PT Index Multifinance',
        'PT Index Syariah',
        'PT Index Capital',
        'PT Index Insurance',
        'PT Index Asset Management'
      ],
      participantColumns: [
        {
          label: 'Employee Name',
          field: 'employee_name',
          sortable: true
        },
        {
          label: 'NIK',
          field: 'employee_id',
          sortable: true
        },
        {
          label: 'Jabatan',
          field: 'position',
          sortable: true
        },
        {
          label: 'Company',
          field: 'company',
          sortable: true
        },
        {
          label: 'Approval',
          field: 'approval_step_label',
          sortable: true
        },
        {
          label: 'Action',
          field: 'action',
          sortable: false,
          width: '100px'
        }
      ]
    }
  },
  watch: {
    selectedCourse: {
      immediate: true,
      handler(newCourse) {
        if (newCourse && newCourse.id) {
          this.selectedStatus = null;
          this.selectedApprovalStep = null;
          this.selectedCourseType = null;
          this.loadCourseDetail(newCourse.id);
          this.loadAllParticipants(newCourse.id);
        }
      }
    },
    selectedCompany() {
      if (this.courseData?.id) {
        this.loadAllParticipants(this.courseData.id);
      }
    },
    selectedStatus() {
      if (this.courseData?.id) {
        this.loadAllParticipants(this.courseData.id);
      }
    },
    selectedApprovalStep() {
      if (this.courseData?.id) {
        this.loadAllParticipants(this.courseData.id);
      }
    },
    selectedCourseType() {
      if (this.courseData?.id) {
        this.loadAllParticipants(this.courseData.id);
      }
    }
  },
  methods: {
    ...mapActions('tnaMitra', [
      'getCourseDetail', 
      'downloadTnaMitraTemplate',
      'uploadTnaMitraFile',
      'getMandatoriedEmployees',
      'getSubmittedParticipants',
      'deleteParticipant',
      'submitParticipants'
    ]),
    compactNumberFormatter,

    companyParams() {
      return this.selectedCompany ? { company: this.selectedCompany } : {};
    },

    normalizeParticipantYear(participant) {
      const parsedYear = Number(
        participant?.planning_year || participant?.year || participant?.training_year || participant?.submitted_year
      );

      if (Number.isInteger(parsedYear) && parsedYear > 0) {
        return parsedYear;
      }

      return this.currentYear;
    },

    getParticipantsByYear(year) {
      return (this.participants || []).filter(
        participant => this.normalizeParticipantYear(participant) === year
      );
    },

    getFilteredParticipantsByYear(year) {
      return this.getParticipantsByYear(year)
        .filter(participant => {
          if (!this.selectedCourseType) return true;
          return this.resolveCourseType(participant) === this.selectedCourseType;
        })
        .filter(participant => {
          if (!this.selectedApprovalStep) return true;
          return this.resolveApprovalStep(participant.tna_status).value === this.selectedApprovalStep;
        })
        .filter(participant => {
          if (!this.selectedStatus) return true;
          return String(participant.tna_status || participant.status || '') === this.selectedStatus;
        })
        .map(participant => ({
          ...participant,
          approval_step_label: this.resolveApprovalStep(participant.tna_status).label
        }));
    },

    withPlanningYear(participant) {
      return {
        ...participant,
        planning_year: this.normalizeParticipantYear(participant)
      };
    },

    participantKey(participant) {
      return `${participant?.employee_id || participant?.nik || participant?.id}-${this.normalizeParticipantYear(participant)}`;
    },

    trainingRequirementLabel(participant) {
      return participant?.is_mandatory ? 'Mandatory' : 'Development';
    },

    resolveCourseType(participant) {
      return participant?.is_mandatory ? 'mandatory' : 'development';
    },

    resolveApprovalStep(status) {
      const normalized = String(status || '').toLowerCase();
      if (normalized.includes('pjo')) return { value: 'pjo', label: 'PJO' };
      if (normalized.includes('admin hse')) return { value: 'hse', label: 'HSE' };
      if (normalized.includes('ktt')) return { value: 'ktt', label: 'KTT' };
      return { value: null, label: '-' };
    },

    formatTnaStatus(status) {
      return String(status || 'submitted')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, char => char.toUpperCase());
    },

    participantStatusLabel(participant) {
      if (!participant?.is_submitted) return participant?.is_new ? 'Draft' : 'Belum diajukan';
      return this.formatTnaStatus(participant.tna_status);
    },

    participantStatusBadgeClass(participant) {
      if (!participant?.is_submitted) return 'bg-secondary';
      const status = String(participant.tna_status || '').toLowerCase();
      if (status === 'rejected') return 'bg-danger';
      if (status.includes('waiting')) return 'bg-warning text-dark';
      if (status === 'approved') return 'bg-success';
      return 'bg-secondary';
    },

    isParticipantLocked(participant) {
      return Boolean(participant?.is_locked || participant?.is_mandatory || participant?.is_submitted);
    },

    participantDeleteTitle(participant) {
      if (participant?.is_submitted) return 'Peserta yang sudah disubmit tidak dapat dihapus.';
      if (participant?.is_mandatory) return 'Peserta mandatory tidak dapat dihapus.';
      return 'Hapus peserta';
    },
    
    async loadCourseDetail(courseId) {
      try {
        await this.getCourseDetail(courseId);
      } catch (error) {
        console.error('Error loading course detail:', error);
        this.$swal('Error', 'Detail pelatihan belum berhasil dimuat.', 'error');
      }
    },
    
    async loadAllParticipants(courseId) {
      try {
        // Load both mandatoried and submitted participants
        const [mandatoriedEmployees, submittedParticipants] = await Promise.all([
          this.getMandatoriedEmployees({
            courseId,
            params: this.companyParams()
          }),
          this.getSubmittedParticipants({
            courseId,
            params: {
              all: true,
              tna_period: this.courseData?.tna_period_id || undefined,
              ...this.companyParams(),
              ...buildMitraFilterParams({
                status: this.selectedStatus,
                approval_step: this.selectedApprovalStep,
                course_type: this.selectedCourseType
              }, { includeParticipantStatusFromApproval: true })
            }
          })
        ]);

        const normalizedMandatoriedEmployees = (mandatoriedEmployees || []).map(this.withPlanningYear);
        const normalizedSubmittedParticipants = (submittedParticipants || []).map(this.withPlanningYear);

        // Merge kedua data, hindari duplikasi berdasarkan NIK dan planning year.
        const allParticipants = [...normalizedMandatoriedEmployees];
        const existingNiks = new Set(
          normalizedMandatoriedEmployees.map(emp => this.participantKey(emp))
        );
        
        normalizedSubmittedParticipants.forEach(participant => {
          const key = this.participantKey(participant);
          if (!existingNiks.has(key)) {
            allParticipants.push(participant);
            existingNiks.add(key);
            return;
          }

          const existingIndex = allParticipants.findIndex(emp => this.participantKey(emp) === key);
          if (existingIndex !== -1) {
            allParticipants.splice(existingIndex, 1, {
              ...allParticipants[existingIndex],
              ...participant,
              is_mandatory: Boolean(allParticipants[existingIndex].is_mandatory || participant.is_mandatory),
              is_submitted: true,
              is_locked: true
            });
          }
        });

        // Set ke store
        this.$store.commit('tnaMitra/SET_PARTICIPANTS', allParticipants);
      } catch (error) {
        console.error('Error loading participants:', error);
      }
    },
    
    handleImageError(event) {
      event.target.src = 'https://dummyimage.com/300x200/FAFAFA/000000&text=Course';
    },
    
    getTrainingTypeIcon(trainingType) {
      const type = trainingType?.toLowerCase() || '';
      
      if (type.includes('certification') && !type.includes('semi')) {
        return 'fe fe-file-text';
      } else if (type.includes('semi')) {
        return 'fe fe-shield';
      } else if (type.includes('training')) {
        return 'fe fe-user';
      }
      
      return 'fe fe-award';
    },

    openAddParticipantModal(year) {
      this.selectedPlanningYear = year || this.currentYear;
      this.$bvModal.show('modal-add-participant');
    },

    confirmDeleteParticipant(participant, year) {
      if (this.isParticipantLocked(participant)) {
        this.$swal('Warning', this.participantDeleteTitle(participant), 'warning');
        return;
      }
      
      this.participantToDelete = {
        ...participant,
        planning_year: year || this.normalizeParticipantYear(participant)
      };
      this.$bvModal.show('modal-delete-participant');
    },

    async onDeleteParticipant() {
      if (this.participantToDelete) {
        try {
          // Delete from store only (no API call)
          this.deleteParticipant({
            participantId: this.participantToDelete.id || this.participantToDelete.employee_id,
            planningYear: this.normalizeParticipantYear(this.participantToDelete)
          });
          this.$swal('Success', 'Peserta berhasil dihapus.', 'success');
          this.participantToDelete = null;
        } catch (error) {
          console.error('Error deleting participant:', error);
          this.$swal('Error', 'Peserta belum berhasil dihapus.', 'error');
        }
      }
    },

    async submitToManager() {
      if (this.newParticipants.length === 0) {
        this.$swal('Warning', 'Tambahkan minimal satu peserta baru terlebih dahulu.', 'warning');
        return;
      }

      this.$swal({
        title: 'Submit Pengajuan?',
        text: `Anda akan submit ${this.newParticipants.length} peserta baru untuk pelatihan "${this.courseData.title}".`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#00d97e',
        cancelButtonColor: '#e63757',
        confirmButtonText: 'Ya, Submit',
        cancelButtonText: 'Batal'
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            // Hit API to submit participants
            await this.submitParticipants({
              courseId: this.courseData.id,
              params: this.companyParams()
            });

            await this.loadAllParticipants(this.courseData.id);
            
            this.$swal({
              title: 'Success',
              text: 'Pengajuan pelatihan berhasil disubmit.',
              icon: 'success',
              confirmButtonColor: '#00d97e'
            }).then(() => {
              // Close modal
              this.$bvModal.hide('modal-tna-mitra-detail');
            });
          } catch (error) {
            console.error('Error submitting participants:', error);
            this.$swal('Error', error.message || 'Data peserta belum berhasil disubmit.', 'error');
          }
        }
      });
    },

    async downloadTemplate(courseId) {
      try {
        const response = await this.downloadTnaMitraTemplate({
          courseId,
          params: this.companyParams()
        });
        
        // Create download link
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `participant_${courseId}_template.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        
        this.$swal("Success", "Template berhasil didownload.", "success");
      } catch (error) {
        console.error('Error downloading template:', error);
        this.$swal("Error", "Template belum berhasil didownload.", "error");
      }
    },

    showUploadModal() {
      this.$bvModal.show('modal-upload-participant');
    },

    onFileChange(event) {
      const file = event.target.files[0];
      if (file) {
        // Check file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
          this.$swal('Error', 'Ukuran file melebihi batas 5MB.', 'error');
          this.clearFile();
          return;
        }
        this.selectedFile = file;
      }
    },

    clearFile() {
      this.selectedFile = null;
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    },

    escapeHtml(value) {
      return String(value ?? '-')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    },

    buildUploadParticipantResultHtml(result) {
      const errors = Array.isArray(result?.errors) ? result.errors : [];
      const maxVisibleErrors = 50;
      const visibleErrors = errors.slice(0, maxVisibleErrors);

      let html = `
        <div style="text-align:left;">
          <p style="margin-bottom:8px;"><strong>Berhasil submit:</strong> ${this.escapeHtml(result?.success || 0)} peserta</p>
          <p style="margin-bottom:8px;"><strong>Pending user sync:</strong> ${this.escapeHtml(result?.pendingUserSync || 0)}</p>
          <p style="margin-bottom:12px;"><strong>Error:</strong> ${this.escapeHtml(errors.length)}</p>
      `;

      if (!errors.length) {
        html += `
          <div style="padding:10px 12px;background:#ecfdf3;border:1px solid #b7ebc6;border-radius:6px;color:#1f7a3f;">
            Upload berhasil tanpa error.
          </div>
        </div>`;
        return html;
      }

      html += `
        <div style="max-height:380px;overflow:auto;border:1px solid #e5e7eb;border-radius:6px;">
          <table style="width:100%;min-width:860px;border-collapse:collapse;font-size:13px;">
            <thead>
              <tr style="background:#fff4e5;color:#7a3f00;">
                <th style="padding:8px;border-bottom:1px solid #e5e7eb;text-align:left;">Row</th>
                <th style="padding:8px;border-bottom:1px solid #e5e7eb;text-align:left;">Sheet</th>
                <th style="padding:8px;border-bottom:1px solid #e5e7eb;text-align:left;">NIK</th>
                <th style="padding:8px;border-bottom:1px solid #e5e7eb;text-align:left;">Pesan Error</th>
              </tr>
            </thead>
            <tbody>
      `;

      visibleErrors.forEach(error => {
        html += `
          <tr>
            <td style="padding:8px;border-bottom:1px solid #f1f3f5;">${this.escapeHtml(error.row)}</td>
            <td style="padding:8px;border-bottom:1px solid #f1f3f5;">${this.escapeHtml(error.sheet)}</td>
            <td style="padding:8px;border-bottom:1px solid #f1f3f5;">${this.escapeHtml(error.nik)}</td>
            <td style="padding:8px;border-bottom:1px solid #f1f3f5;">${this.escapeHtml(error.message || error.reason || error.error)}</td>
          </tr>
        `;
      });

      html += `
            </tbody>
          </table>
        </div>
      `;

      if (errors.length > maxVisibleErrors) {
        html += `<p style="margin-top:10px;color:#6b7280;">Masih ada ${this.escapeHtml(errors.length - maxVisibleErrors)} error lain. Perbaiki error yang tampil dulu, lalu upload ulang.</p>`;
      }

      html += '</div>';
      return html;
    },

    async uploadFile() {
      if (!this.selectedFile) {
        this.$swal("Error", "Pilih file Excel terlebih dahulu.", "error");
        return;
      }

      this.isUploading = true;
      
      try {
        const formData = new FormData();
        formData.append('file', this.selectedFile);

        const response = await this.uploadTnaMitraFile({
          courseId: this.courseData.id,
          formData,
          params: this.companyParams()
        });
        
        const result = response?.data || response;
        if (result) {
          const { errorsData = 0, errors = [] } = result;
          
          if (errorsData > 0 && errors.length > 0) {
            this.$swal({
              title: "Upload Selesai dengan Error",
              html: this.buildUploadParticipantResultHtml(result),
              icon: "warning",
              confirmButtonText: "OK",
              confirmButtonColor: '#f6c343',
              width: '1100px'
            }).then(() => {
               this.loadAllParticipants(this.courseData.id);
              this.$bvModal.hide("modal-upload-participant");
              this.clearFile();
            });
          } 
          else {
            this.$swal({
              title: "Upload Berhasil",
              html: this.buildUploadParticipantResultHtml(result),
              icon: "success",
              confirmButtonText: "OK",
              confirmButtonColor: '#00d97e',
              width: '720px'
            }).then(() => {
               this.loadAllParticipants(this.courseData.id);
              this.$bvModal.hide('modal-upload-participant');
              this.clearFile();
            });
          }
        } else {
          // Fallback for unexpected response format
          this.$swal({
            title: "Upload Berhasil",
            text: "File berhasil diupload",
            icon: "success",
            confirmButtonText: "OK"
          }).then(() => {
             this.loadAllParticipants(this.courseData.id);
            this.$bvModal.hide('modal-upload-participant');
            this.clearFile();
          });
        }

      } catch (error) {
        console.error('Error uploading file:', error);
        let errorMessage = "File belum berhasil diupload. Periksa format file lalu coba lagi.";
        
        // Handle API error response
        if (error.response && error.response.data) {
          const errorData = error.response.data;
          if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.error) {
            errorMessage = errorData.error;
          }
        }
        
        this.$swal("Error", errorMessage, "error");
      } finally {
        this.isUploading = false;
      }
    },

    resetUploadForm() {
      this.uploadForm.company = null;
      this.clearFile();
    }
  },
  
  mounted() {
    console.log('✅ Modal TNA Detail mounted');
    console.log('📝 Initial selectedCourse:', this.selectedCourse);
  }
}
</script>

<style scoped>
/* Gap Utilities */
.gap-2 {
  gap: 0.5rem;
}

/* Badge Styles */
.badge-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border-radius: 50%;
  font-size: 0.75rem;
}

.badge-custom {
  padding: 0.35rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 600;
  border-radius: 0.35rem;
}

/* Soft Badge Colors */
.bg-danger-soft {
  background-color: #fce4e4;
}

.text-danger {
  color: #e63757 !important;
}

.bg-warning-soft {
  background-color: #fef5e4;
}

.text-warning {
  color: #f6c343 !important;
}

.bg-info-soft {
  background-color: #e4f4f7;
}

.text-info {
  color: #39afd1 !important;
}

/* Tab Styles */
.nav-tabs {
  border-bottom: 1px solid #dee2e6;
}

.nav-tabs .nav-link {
  color: #718096;
  font-weight: 500;
  border: none !important;
  border-bottom: 3px solid transparent !important;
  padding: 0.75rem 1rem;
  background: transparent !important;
}

.nav-tabs .nav-link:hover {
  border-color: transparent transparent #dee2e6 transparent !important;
  color: #4a5568;
}

.nav-tabs .nav-link.active {
  color: #e63757 !important;
  font-weight: 600;
  border-bottom: 3px solid #e63757 !important;
  background: transparent !important;
}

.tab-content {
  padding-top: 1rem;
}

.planning-section {
  padding: 1rem;
  border: 1px solid #f1f1f1;
  border-radius: 0.75rem;
  margin-bottom: 1rem;
}

.planning-year-title {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
}

.planning-year-icon {
  width: 46px;
  height: 46px;
  border-radius: 0.5rem;
  background-color: #f7eef2;
  color: #bd345f;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.15rem;
}

.planning-year-text {
  color: #111827;
  font-weight: 700;
  line-height: 1.2;
}

/* Table Styles */
.table {
  margin-bottom: 0;
}

.table thead th {
  border-bottom: 2px solid #dee2e6;
  padding: 0.75rem 1rem;
  font-size: 0.75rem;
  letter-spacing: 0.5px;
}

.table tbody td {
  padding: 1rem;
  vertical-align: middle;
  border-bottom: 1px solid #f1f1f1;
}

.table-hover tbody tr:hover {
  background-color: #f8f9fa;
}

/* Vue Good Table Custom Styles */
::v-deep .vgt-table {
  border: none !important;
}

::v-deep .vgt-table thead th {
  background-color: #f8f9fa !important;
  color: #6c757d !important;
  font-size: 0.75rem !important;
  font-weight: 400 !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 0.75rem 1rem !important;
  border-bottom: 2px solid #dee2e6 !important;
}

::v-deep .vgt-table tbody td {
  padding: 1rem !important;
  vertical-align: middle !important;
  border-bottom: 1px solid #f1f1f1 !important;
}

::v-deep .vgt-wrap__footer {
  background: transparent !important;
  border: none !important;
  padding: 1rem 0 !important;
}

::v-deep .vgt-wrap__footer .footer__row-count {
  font-size: 0.875rem !important;
}

::v-deep .vgt-wrap__footer .footer__navigation {
  font-size: 0.875rem !important;
}

::v-deep .vgt-wrap__footer .footer__navigation__page-btn {
  padding: 0.25rem 0.5rem !important;
  border-radius: 0.25rem !important;
}

::v-deep .vgt-table.striped tbody tr:nth-child(odd) {
  background-color: transparent !important;
}

::v-deep .vgt-table.striped tbody tr:nth-child(even) {
  background-color: transparent !important;
}

::v-deep .vgt-table tbody tr:hover {
  background-color: #f8f9fa !important;
}
</style>
